/**
 * Error Tracking Service — captura erros globais e envia em batch para o backend.
 *
 * Equivalente Angular: ErrorHandler global + HttpInterceptor para retry.
 */

import { getUserIdOrNull } from '../lib/pwa/userScope';

interface FrontendError {
  message: string;
  source?: string;
  lineno?: number;
  colno?: number;
  stack?: string;
  component_stack?: string;
  url?: string;
  user_agent?: string;
  screen_resolution?: string;
  timestamp?: string;
  request_id?: string;
}

const BATCH_SIZE = 10;
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 100;
const STORAGE_KEY_PREFIX = 'jurisapp:error_queue';
const ANONYMOUS_SCOPE = 'anon';
const DEDUP_WINDOW_MS = 60_000; // 1 minuto

/**
 * Resolve a chave de localStorage escopada por usuário. Erros gerados antes do
 * login (landing, fluxo de auth) ficam sob o escopo anônimo. Após o login, a
 * fila persistida é isolada por userId — assim a fila de um usuário não
 * sobrevive ao logout para ser reenviada sob o token do próximo usuário.
 */
function getStorageKey(): string {
  const userId = getUserIdOrNull();
  return `${STORAGE_KEY_PREFIX}:${userId ?? ANONYMOUS_SCOPE}`;
}

/**
 * Remove URLs (query string e hash) que podem carregar identificadores
 * processuais sensíveis. Mantém apenas origin + pathname para diagnóstico.
 */
function sanitizeUrl(rawUrl: string | undefined): string | undefined {
  if (!rawUrl) return undefined;
  try {
    const parsed = new URL(rawUrl, window.location.origin);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return undefined;
  }
}

const errorQueue: FrontendError[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
const recentHashes = new Set<string>();
let lastRequestId: string | null = null;

/**
 * Seta o request_id mais recente (chamado pelo interceptor do axios).
 */
export function setLastRequestId(requestId: string | null): void {
  lastRequestId = requestId;
}

function hashError(error: FrontendError): string {
  // Inclui componentStack para nao colapsar erros de render React iguais
  // (mesma message minificada, ex: "Minified React error #185") quando
  // disparam de componentes distintos.
  const stackHead = error.component_stack?.split('\n').slice(0, 3).join('|') ?? '';
  return `${error.message}:${error.source}:${error.lineno}:${stackHead}`;
}

function isDuplicate(error: FrontendError): boolean {
  const hash = hashError(error);
  if (recentHashes.has(hash)) return true;
  recentHashes.add(hash);
  setTimeout(() => recentHashes.delete(hash), DEDUP_WINDOW_MS);
  return false;
}

export function enqueueError(error: FrontendError): void {
  // Sanitiza a URL centralmente (remove query/hash com possível PII processual).
  const sanitized: FrontendError = { ...error, url: sanitizeUrl(error.url) };
  if (isDuplicate(sanitized)) return;
  if (errorQueue.length >= MAX_QUEUE_SIZE) {
    errorQueue.shift(); // descartar o mais antigo
  }
  errorQueue.push(sanitized);

  if (errorQueue.length >= BATCH_SIZE) {
    flushErrors();
  }
}

function loadOfflineQueue(): void {
  try {
    const key = getStorageKey();
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored) as FrontendError[];
      errorQueue.push(...parsed);
      localStorage.removeItem(key);
    }
  } catch {
    // localStorage pode estar cheio ou indisponível
  }
}

function saveOfflineQueue(): void {
  if (errorQueue.length === 0) return;
  try {
    localStorage.setItem(getStorageKey(), JSON.stringify(errorQueue));
  } catch {
    // localStorage cheio
  }
}

/**
 * Limpa a fila de erros (memória + localStorage escopado) do usuário indicado.
 * Chamado no logout para que erros pendentes — que podem conter PII — não
 * sobrevivam à troca de conta e sejam reenviados sob o token do próximo usuário.
 */
export function clearErrorQueue(userId: string): void {
  errorQueue.length = 0;
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}:${userId}`);
  } catch {
    // localStorage indisponível
  }
}

async function flushErrors(): Promise<void> {
  if (errorQueue.length === 0) return;

  const batch = errorQueue.splice(0, BATCH_SIZE);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    // Token é opcional: o endpoint aceita reports anônimos (landing page,
    // fluxo de login, sessão expirada). Quando há token, o backend associa
    // ao user_id; sem token, fica como anônimo (rate-limit por IP cuida do abuso).
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch('/api/v1/logs/frontend', {
      method: 'POST',
      headers,
      body: JSON.stringify({ errors: batch }),
    });

    if (!response.ok) {
      // Re-enqueue on failure
      errorQueue.unshift(...batch);
      saveOfflineQueue();
    }
  } catch {
    // Offline ou erro de rede — salvar para retry
    errorQueue.unshift(...batch);
    saveOfflineQueue();
  }
}

function getAuthToken(): string | null {
  // Buscar token do Auth0 SDK via cache do localStorage
  // O @auth0/auth0-spa-js armazena tokens em cache interno
  // Usar uma heurística para encontrar o token
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('@@auth0spajs@@')) {
        const data = JSON.parse(localStorage.getItem(key) || '');
        if (data?.body?.access_token) {
          return data.body.access_token;
        }
      }
    }
  } catch {
    // Silenciar
  }
  return null;
}

/**
 * Inicializa o error tracking global.
 * Deve ser chamado uma vez no bootstrap da aplicação (antes do React render).
 */
export function initErrorTracking(): void {
  loadOfflineQueue();

  const screenRes = `${window.screen.width}x${window.screen.height}`;

  // Capturar erros não tratados
  window.addEventListener('error', (event: ErrorEvent) => {
    enqueueError({
      message: event.message,
      source: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      stack: event.error?.stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
      screen_resolution: screenRes,
      timestamp: new Date().toISOString(),
      request_id: lastRequestId ?? undefined,
    });
  });

  // Capturar promises rejeitadas não tratadas
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    const error = event.reason;
    enqueueError({
      message: error?.message || String(error),
      stack: error?.stack,
      url: window.location.href,
      user_agent: navigator.userAgent,
      screen_resolution: screenRes,
      timestamp: new Date().toISOString(),
      request_id: lastRequestId ?? undefined,
    });
  });

  // Flush periódico
  flushTimer = setInterval(flushErrors, FLUSH_INTERVAL_MS);

  // Flush ao fechar a página
  window.addEventListener('beforeunload', () => {
    if (flushTimer) clearInterval(flushTimer);
    saveOfflineQueue();
  });
}
